/**
 * Request payloads for the client-side `/api/files/:uuid` route (see
 * `routes/api.files.$uuid.ts`), which fetchers submit to.
 *
 * These live apart from the route module so that callers can build a payload
 * without importing the route itself. The route pulls in the app's file-loading
 * and grid modules, which import the action specs, which import
 * `app/actions.ts` — importing the route from there closes that ring and makes
 * module evaluation order decide whether the app boots.
 *
 * Keep this module free of imports.
 */

export type FileAction = {
  response: { ok: boolean; uuid?: string } | null;
  'request.delete': ReturnType<typeof getActionFileDelete>;
  'request.download': ReturnType<typeof getActionFileDownload>;
  'request.duplicate': ReturnType<typeof getActionFileDuplicate>;
  'request.move': ReturnType<typeof getActionFileMove>;
  'request.rename': {
    action: 'rename';
    name: string;
  };
  request:
    | FileAction['request.delete']
    | FileAction['request.download']
    | FileAction['request.duplicate']
    | FileAction['request.move']
    | FileAction['request.rename'];
};

/**
 * @param ownerUserId - The ID of the user where you want to move the file
 * (as a private file on the team). `null` moves it to the team's public files.
 * @returns
 */
export const getActionFileMove = (ownerUserId: number | null) => {
  return {
    action: 'move' as const,
    ownerUserId,
  };
};

/**
 * @param {Object} args
 * @param {boolean} args.redirect - Whether to redirect the user to the new file after duplication
 * @param {boolean} args.isPrivate - Whether the file is private to the user on the team where its created
 * @param {string} [args.folderUuid] - Optional folder to place the duplicated file into (preserves
 *   the source file's folder membership when duplicating from the dashboard)
 * @returns
 */
export const getActionFileDuplicate = ({
  isPrivate,
  redirect,
  teamUuid,
  checkpointDataUrl,
  checkpointVersion,
  folderUuid,
}: {
  isPrivate: boolean;
  redirect: boolean;
  teamUuid: string;
  checkpointDataUrl?: string;
  checkpointVersion?: string;
  folderUuid?: string;
}) => {
  return {
    action: 'duplicate' as const,
    isPrivate,
    redirect,
    teamUuid,
    ...(checkpointDataUrl ? { checkpointDataUrl } : {}),
    ...(checkpointVersion ? { checkpointVersion } : {}),
    ...(folderUuid ? { folderUuid } : {}),
  };
};

export const getActionFileDelete = ({ userEmail, redirect }: { userEmail: string; redirect: boolean }) => {
  return {
    action: 'delete' as const,
    userEmail,
    redirect,
  };
};

export const getActionFileDownload = ({ checkpointDataUrl }: { checkpointDataUrl?: string } = {}) => {
  return {
    action: 'download' as const,
    ...(checkpointDataUrl ? { checkpointDataUrl } : {}),
  };
};
